<script>
/* global FB */
import InboxReconnectionRequired from '../components/InboxReconnectionRequired.vue';
import { useAlert } from 'dashboard/composables';

import { loadScript } from 'dashboard/helper/DOMHelpers';
import * as Sentry from '@sentry/vue';

export default {
  components: {
    InboxReconnectionRequired,
  },
  props: {
    inbox: {
      type: Object,
      required: true,
    },
  },
  computed: {
    inboxId() {
      return this.inbox.id;
    },
  },
  mounted() {
    window.fbAsyncInit = this.runFBInit;
  },
  methods: {
    runFBInit() {
      try {
        const fbAppId = window.chatwootConfig?.fbAppId;
        const fbApiVersion = window.chatwootConfig?.fbApiVersion || 'v18.0';

        if (!fbAppId) {
          // eslint-disable-next-line no-console
          console.error('Facebook App ID no está configurado');
          return;
        }

        FB.init({
          appId: fbAppId,
          xfbml: true,
          version: fbApiVersion,
          status: true,
        });
        window.fbSDKLoaded = true;
        FB.AppEvents.logPageView();

        // Resolve the init promise if it exists
        if (window.fbInitPromise) {
          window.fbInitResolve();
          window.fbInitPromise = null;
          window.fbInitResolve = null;
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error initializing Facebook SDK:', error);
        if (window.fbInitPromise) {
          window.fbInitReject(error);
          window.fbInitPromise = null;
          window.fbInitResolve = null;
          window.fbInitReject = null;
        }
      }
    },

    waitForFBInit() {
      return new Promise((resolve, reject) => {
        // If FB is already initialized, resolve immediately
        if (window.FB && window.fbSDKLoaded) {
          resolve();
          return;
        }

        // If SDK is already loaded but not initialized, initialize it
        if (window.FB && !window.fbSDKLoaded) {
          this.runFBInit();
          // Wait a bit for init to complete
          setTimeout(() => {
            if (window.fbSDKLoaded) {
              resolve();
            } else {
              reject(new Error('Facebook SDK initialization timeout'));
            }
          }, 1000);
          return;
        }

        // Set up promise for async init
        window.fbInitPromise = true;
        window.fbInitResolve = resolve;
        window.fbInitReject = reject;

        // If fbAsyncInit hasn't been called yet, call runFBInit manually
        if (!window.fbAsyncInitCalled) {
          setTimeout(() => {
            if (window.FB && !window.fbSDKLoaded) {
              this.runFBInit();
            }
          }, 100);
        }

        // Timeout after 10 seconds
        setTimeout(() => {
          if (window.fbInitPromise) {
            window.fbInitPromise = null;
            window.fbInitResolve = null;
            window.fbInitReject = null;
            reject(new Error('Facebook SDK initialization timeout'));
          }
        }, 10000);
      });
    },

    async loadFBsdk() {
      return loadScript('https://connect.facebook.net/en_US/sdk.js', {
        id: 'facebook-jssdk',
      });
    },

    async startLogin() {
      try {
        // Validate Facebook App ID before proceeding
        if (!window.chatwootConfig?.fbAppId) {
          useAlert('Facebook App ID no está configurado. Por favor, configura FB_APP_ID en Super Admin.');
          return;
        }

        // Load the SDK
        await this.loadFBsdk();

        // Wait for FB to be initialized
        await this.waitForFBInit();

        // Verify FB is ready before attempting login
        if (!window.FB || !window.fbSDKLoaded) {
          useAlert('Facebook SDK no se inicializó correctamente');
          return;
        }

        // Now safe to attempt login
        this.tryFBlogin();
      } catch (error) {
        if (error.name === 'ScriptLoaderError') {
          useAlert(this.$t('INBOX_MGMT.DETAILS.ERROR_FB_LOADING'));
        } else if (error.message.includes('App ID')) {
          useAlert(error.message);
        } else {
          // eslint-disable-next-line no-console
          console.error('Facebook initialization error:', error);
          Sentry.captureException(error);
          useAlert(this.$t('INBOX_MGMT.DETAILS.ERROR_FB_AUTH'));
        }
      }
    },

    tryFBlogin() {
      if (!window.FB || !window.fbSDKLoaded) {
        useAlert('Facebook SDK no está inicializado');
        return;
      }

      FB.login(
        response => {
          if (response.status === 'connected') {
            this.reauthorizeFBPage(response.authResponse.accessToken);
          } else if (response.status === 'not_authorized') {
            // The person is logged into Facebook, but not your app.
            useAlert(this.$t('INBOX_MGMT.DETAILS.ERROR_FB_AUTH'));
          } else {
            // The person is not logged into Facebook, so we're not sure if
            // they are logged into this app or not.
            useAlert(this.$t('INBOX_MGMT.DETAILS.ERROR_FB_AUTH'));
          }
        },
        {
          scope:
            'pages_manage_metadata,business_management,pages_messaging,instagram_basic,pages_show_list,pages_read_engagement,instagram_manage_messages',
          auth_type: 'reauthorize',
        }
      );
    },
    async reauthorizeFBPage(omniauthToken) {
      try {
        await this.$store.dispatch('inboxes/reauthorizeFacebookPage', {
          omniauthToken,
          inboxId: this.inboxId,
        });
        useAlert(this.$t('INBOX_MGMT.FACEBOOK_REAUTHORIZE.MESSAGE_SUCCESS'));
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.FACEBOOK_REAUTHORIZE.MESSAGE_ERROR'));
      }
    },
  },
};
</script>

<template>
  <InboxReconnectionRequired class="mx-6" @reauthorize="startLogin" />
</template>

<style lang="scss" scoped>
.fb--login {
  img {
    max-width: 240px;
    padding: 1rem 0;
  }
}
</style>
